/* eslint-disable react-native/no-inline-styles */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View, ScrollView } from 'react-native';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';

import * as Layout from '@components/screen/Layout';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import MatrixTier from '@components/setting/matrix-approval/MatrixTier';
import {
  SociollaForm,
  SociollaInput,
  SociollaSelectBox,
  SociollaSubmitButton,
} from '@components/sociolla/form';
import ProgressBar from '@components/global/ProgressBar';
import BackButton from '@components/navbar/BackButton';

import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import {
  getMatrixApprovalDetail,
  updateMatrixApproval,
  createMatrixApproval,
  createMatrixTier,
} from '@api/ms-b2b';
import useApi from '@/hooks/useApi';
import {
  hasNoAccess,
  setSavedFilter,
  getSavedFilter,
  getConstant,
  generateConstant,
} from '@global/helpers';
import { showPopup } from '@/store/modal';

const validationSchema = Yup.object().shape({
  applicable_for: Yup.mixed()
    .required('Matrix Approval Type is required.')
    .label('Matrix Approval Type'),
  description: Yup.string()
    .required('Description is required.')
    .label('Description'),
});

let form = null;

const MatrixApprovalDetailScreen = (props) => {
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [page, setPage] = useState(1);
  const [description, setDescription] = useState('');
  const matrix = useApi(getMatrixApprovalDetail);
  const totalPage = 2;

  const matrixRef = useRef(null);

  const dispatch = useDispatch();
  const hasAccess = !hasNoAccess([
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
  ]);

  const disabledButton = useMemo(() => {
    if (description && description != matrix.data.description) {
      return false;
    } else {
      return true;
    }
  }, [description]);

  const button = useMemo(() => {
    if (page === 1) {
      return [
        {
          title: 'Next',
          disabled: isEdit ? false : disabledButton,
          onPress: () => form.handleSubmit(),
        },
      ];
    } else {
      return hasAccess
        ? [
            {
              title: 'Add Tier',
              disabled: isEdit ? false : disabledButton,
              onPress: () => matrixRef?.current(),
            },
          ]
        : [];
    }
  }, [page, isEdit, disabledButton]);

  const setRules = (rules) => {
    const newData = JSON.parse(JSON.stringify(matrix.data));
    const type = generateConstant(
      'matrix_approval',
      newData.applicable_for,
      'value',
    );
    const rule = rules.map((rl) => {
      if (type.type == 'amount') {
        return {
          min_amount: Number(rl.min_amount),
          max_amount: Number(rl.max_amount),
          approver: rl.approver,
        };
      } else {
        return { applicable_on: rl.applicable_on, approver: rl.approver };
      }
    });
    matrix.updateData({ ...newData, rule });
  };

  // Temporary event
  const tempApi = async (data) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const newData = JSON.parse(JSON.stringify(matrix.data));
      const rules = newData.rule || [];
      let type = data.applicable_for;
      if (isEdit) {
        type = generateConstant(
          'matrix_approval',
          data.applicable_for,
          'label',
        );
      }
      const payload = {
        description: data.description,
        applicable_for: type.value,
        type: type.type,
        rule: rules,
      };
      if (isEdit) {
        const addPayload = rules.filter((rl) => !rl._id);
        const editayload = rules.filter((rl) => rl._id);
        if (addPayload.length > 0) {
          await createMatrixTier(matrix.data._id, { rule: addPayload });
        }
        if (editayload.length > 0) {
          await updateMatrixApproval(matrix.data._id, {
            description: data.description,
            rule: editayload,
          });
        }
        Toast.show({
          text1: 'Successfully edited data',
          type: 'success',
        });
      } else {
        createMatrixApproval(payload).then((res) => {
          if (res.status == 200) {
            Toast.show({
              text1: 'Successfully added data',
              type: 'success',
            });
            matrix.updateData(res.data.data);
            props.navigation.replace('MatrixApprovalDetailScreen', {
              id: res.data.data._id,
              name: res.data.data.applicable_for,
              prev_page: '',
              tab: '',
            });
          }
        });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (e) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: 'A system error occurred',
        type: 'error',
      });
    }
  };

  // Direct event
  const handleSubmit = async (data) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      let type = data.applicable_for;
      if (isEdit) {
        let tracker = 'label';
        if (
          [
            'target_sales_value',
            'target_product_focus',
            'target_doors',
          ].includes(type)
        ) {
          tracker = 'value';
        }
        type = generateConstant(
          'matrix_approval',
          data.applicable_for,
          tracker,
        );
      }
      const payload = {
        description: data.description,
        applicable_for: type.value,
        type: type.type,
      };
      if (
        ['target_sales_value', 'target_product_focus', 'target_doors'].includes(
          type.value,
        )
      ) {
        payload.tier_type = [
          'national',
          'channel',
          'branch',
          'sales team & sales man',
        ];
      }
      if (type.value == 'promotion-rules') {
        payload.tier_type = [
          'extra_discount',
          'regular_discount',
          'buy_x_get_x_and_buy_x_get_y',
        ];
      }
      if (isEdit) {
        if (payload.description !== matrix.data.description) {
          await updateMatrixApproval(matrix.data._id, {
            description: data.description,
            ...(payload.tier_type?.length
              ? { tier_type: payload.tier_type }
              : {}),
          }).then((res) => {
            if (res.status == 200) {
              Toast.show({
                text1: 'Successfully updated data',
                type: 'success',
              });
              matrix.updateData(res.data.data);
              if (isWeb) {
                props.navigation.replace('MatrixApprovalDetailScreen', {
                  id: res.data.data._id,
                  name: res.data.data.applicable_for,
                  prev_page: '',
                  tab: '',
                });
              } else {
                setPage(2);
              }
            }
          });
        } else {
          setPage(2);
        }
      } else {
        await createMatrixApproval(payload).then((res) => {
          if (res.status == 200) {
            Toast.show({
              text1: 'Successfully added data',
              type: 'success',
            });
            matrix.updateData(res.data.data);
            if (isWeb) {
              props.navigation.replace('MatrixApprovalDetailScreen', {
                id: res.data.data._id,
                name: res.data.data.applicable_for,
                prev_page: '',
                tab: '',
              });
            } else {
              setPage(2);
            }
          }
        });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (e) {
      let message = 'A system error occurred';
      if (e?.response?.data?.message) {
        message = e.response.data.message;
      }
      if (message.includes('duplicate')) {
        message = 'Duplicate types of matrix approval';
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: message,
        type: 'error',
      });
    }
  };

  useEffect(() => {
    const matrixId = props.route.params?.id || '';
    async function onLoad() {
      if (matrixId && (matrixId == 'view' || matrixId == 'add')) {
        matrix.updateData({});
        setIsView(Boolean(matrixId == 'view'));
        setIsReady(true);
      } else if (matrixId) {
        const res = await matrix.request(matrixId);
        if (res.status == 200) {
          setIsEdit(true);
          setTimeout(() => setIsReady(true), 500);
        }
      }
    }
    onLoad();
  }, [props]);

  useEffect(() => {
    if (page && props.navigation) {
      props.navigation.setOptions({
        headerLeft: () => (
          <BackButton
            {...props}
            onPress={() => {
              if (page === 1) {
                props.navigation.navigate('MatrixApprovalScreen');
              } else {
                setPage(1);
              }
            }}
          />
        ),
      });
    }
  }, [page, props]);

  if (!isReady) {
    return (
      <Layout.Container>
        <Layout.Wrapper rounded>
          <Layout.Loader />
        </Layout.Wrapper>
      </Layout.Container>
    );
  }

  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Wrapper fullWidth white={false} hasProgressBar={true}>
          <Layout.Back
            title="Matrix Approval"
            onCallback={() => props.navigation.navigate('MatrixApprovalScreen')}
          />
          <Layout.Title
            title={
              isEdit ? 'Update Matrix Approval' : 'Create Matrix Approval'
            }>
            {!isView && hasAccess && (
              <Button
                rounded={true}
                buttonStyle={{ width: 120 }}
                disabledStyle={{ width: 120 }}
                disabled={disabledButton}
                title={
                  <SociollaText
                    type="bold"
                    style={{
                      fontSize: 14,
                      letterSpacing: 0.5,
                      color: Colors.white,
                    }}>
                    {isEdit ? 'Update' : 'Add'}
                  </SociollaText>
                }
                onPress={() => form.handleSubmit()}
              />
            )}
          </Layout.Title>
        </Layout.Wrapper>
      ) : (
        <ProgressBar key="progress-bar" page={page} totalPage={totalPage} />
      )}
      <Layout.Wrapper
        rounded
        hasProgressBar
        bottomSpace={!isWeb ? 70 : 0}
        fullWidth
        isHeight>
        <ScrollView>
          {page == 1 && (
            <Layout.FormWrapper
              style={{
                flexDirection: isWeb ? 'row' : 'column',
                paddingHorizontal: 15,
                ...(isWeb
                  ? {
                      paddingTop: 20,
                      paddingVertical: 20,
                    }
                  : {
                      paddingTop: 10,
                    }),
              }}>
              <View style={{ width: isWeb ? '50%' : '100%' }}>
                <SociollaForm
                  initialValues={{
                    applicable_for: isEdit ? props.route.params.name : '',
                    description: isEdit ? matrix.data.description : '',
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  innerRef={(formik) => (form = formik)}>
                  <Layout.FormGroup>
                    <SociollaSelectBox
                      name="applicable_for"
                      label="Matrix Approval Type"
                      trackBy="value"
                      labelKey="label"
                      options={getConstant('matrix_approval')}
                      disabled={isEdit || isView}
                      editable={!isEdit && !isView}
                      isRequired
                    />
                  </Layout.FormGroup>
                  <Layout.FormGroup>
                    <SociollaInput
                      multiline={true}
                      numberOfLines={4}
                      label="Description"
                      name="description"
                      disabled={isView}
                      onChange={(val) => setDescription(val)}
                      isRequired
                    />
                  </Layout.FormGroup>
                </SociollaForm>
              </View>
              <View style={{ width: isWeb ? '50%' : '100%' }} />
            </Layout.FormWrapper>
          )}
          {(isWeb || page == 2) && (isEdit || isView) && (
            <MatrixTier
              data={matrix.data}
              isView={isView}
              setRules={(rules) => setRules(rules)}
              matrixRef={matrixRef}
            />
          )}
        </ScrollView>
      </Layout.Wrapper>
      {!isWeb && (
        <Layout.ButtonSticky show route={props?.route} buttons={button} />
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({});

MatrixApprovalDetailScreen.propTypes = {
  children: PropTypes.node,
  route: PropTypes.object,
  navigation: PropTypes.object,
};

MatrixApprovalDetailScreen.defaultProps = {
  children: <></>,
  route: {},
  navigation: {},
};

export default MatrixApprovalDetailScreen;
